import type { UniverseId } from '@/domain/universes/universe.interface';
import type { AccessLevel } from '@/interfaces/Access.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import {
    momentDateToUnzonedDateTime,
    type UnzonedDate,
    type UnzonedDateTime,
    unzonedDateTimeToApiDateFormat,
    unzonedDateTimeToMomentDate,
    unzonedDateToApiDateFormat
} from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import { UnitCode } from '@/interfaces/Unit.interface';
import type { APIError } from '@/providers/errors.constants';
import { UnitProvider } from '@/providers/unit/unit.provider';
import {
    convertPaginatedRequestParamsToString,
    type CoreApiPaginatedRequestParams,
    type CoreApiPaginatedResponse,
    CoreApiVersionPath,
    RosaCoreApiService
} from '@/rosa';
import { restartOn } from '@/utils/rxjs/operators/restart-on.operator';
import { inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, map, type Observable, Subject } from 'rxjs';
import {
    AlertOccupancyMode,
    type AlertScenarioId,
    type AlertScenarioStatus,
    type AlertScenarioType,
    type AlertStatPeriodCode,
    type AlertValidation
} from '../../alerts.interface';
import type {
    DriftAlertsAnalysis,
    DriftAlertScenarioCreation,
    DriftGlobalStats,
    DriftMeasureLevel,
    DriftScenario,
    DriftScenarioBuildingLevelAbruptStat,
    DriftScenarioBuildingLevelGradualStat,
    DriftScenarioFilters,
    DriftScenarioSenLevelAbruptStat,
    DriftScenarioSenLevelGradualStat,
    DriftScenarioSensorStat,
    DriftScenarioSummary,
    DriftScenarioUpdate,
    DriftSensitivityLevel,
    DriftTargetMeasure
} from '../drift-scenario.interface';

export interface DriftScenarioSummaryResponse {
    alertScenarioId: AlertScenarioId;
    scenarioType: AlertScenarioType;
    name: string;
    universeId: UniverseId;
    targetMeasure: DriftTargetMeasure;
    measureLevel: DriftMeasureLevel;
    isActiveInOccupation: boolean;
    isActiveInInoccupation: boolean;
    status: AlertScenarioStatus;
    access: AccessLevel;
    abruptAlertCount: number;
    impactedBuildingCount: number | null;
    buildingCount: number | null;
    consumptionImpactedBuildingPercent: number | null;
    impactedSensorCount: number | null;
    sensorCount: number | null;
    consumptionImpactedSensorPercent: number | null;
    abruptAlertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
    alertedVal: number;
    alertedValPercent: number;
    alertedValPercentProgress: number | null;
}

const sortedBackendDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreDriftScenarioService {
    private _rosaCoreApiService = inject(RosaCoreApiService);
    private _unitProvider = inject(UnitProvider);

    private _alertScenarioSaved$ = new Subject<AlertScenarioId>();

    createDriftAlertScenario(driftScenarioCreation: DriftAlertScenarioCreation): Promise<AlertScenarioId> {
        interface CreateDriftAlertScenarioDto {
            name: string;
            universeId: UniverseId;
            targetMeasure: DriftTargetMeasure;
            measureLevel: DriftMeasureLevel;
        }

        const createDriftScenarioAlert: CreateDriftAlertScenarioDto = {
            ...driftScenarioCreation
        };

        return firstValueFrom(
            this._rosaCoreApiService.post<AlertScenarioId>(`${CoreApiVersionPath.V5}alerts/scenario/drift`, createDriftScenarioAlert)
        );
    }

    getDriftScenario$(scenarioId: AlertScenarioId): Observable<DriftScenario> {
        interface RouteResponse {
            alertScenarioId: AlertScenarioId;
            scenarioType: AlertScenarioType.DRIFT;
            scenarioStatus: AlertScenarioStatus;
            name: string;
            access: AccessLevel;
            universeId: UniverseId;
            measureLevel: DriftMeasureLevel;
            targetMeasure: DriftTargetMeasure;
            sensitivityLevel: DriftSensitivityLevel;
            periodInfos: {
                isActiveInOccupation: boolean;
                isActiveInInoccupation: boolean;
                isActiveDuringSchoolHolidays: boolean;
                isActiveDuringPublicHolidays: boolean;
                periodStart: {
                    day: number;
                    month: number;
                };
                periodEnd: {
                    day: number;
                    month: number;
                };
                daysRanges: Record<
                    string,
                    {
                        isActivated: boolean;
                        ranges: {
                            start: string;
                            end: string;
                        }[];
                    }
                >;
            };
        }

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenario/${scenarioId}`).pipe(
            restartOn(this._alertScenarioSaved$.pipe(filter(id => id === scenarioId))),
            map(s => ({
                scenario: {
                    scenarioId: s.alertScenarioId,
                    type: s.scenarioType,
                    status: s.scenarioStatus,
                    name: s.name,
                    access: s.access
                },
                driftConfig: {
                    universeId: s.universeId,
                    targetMeasure: s.targetMeasure,
                    measureLevel: s.measureLevel,
                    sensitivityLevel: s.sensitivityLevel,
                    activity: {
                        occupancy:
                            s.periodInfos.isActiveInInoccupation && s.periodInfos.isActiveInOccupation
                                ? AlertOccupancyMode.ALL
                                : s.periodInfos.isActiveInInoccupation
                                  ? AlertOccupancyMode.INOCCUPANCY
                                  : s.periodInfos.isActiveInOccupation
                                    ? AlertOccupancyMode.OCCUPANCY
                                    : AlertOccupancyMode.ALL,
                        alertHolidays: s.periodInfos.isActiveDuringSchoolHolidays,
                        alertPublicHolidays: s.periodInfos.isActiveDuringPublicHolidays,
                        periodStart: s.periodInfos.periodStart,
                        periodEnd: s.periodInfos.periodEnd,
                        weeklyRanges: Object.entries(s.periodInfos.daysRanges || {})
                            .sort(([day1], [day2]) => {
                                return sortedBackendDays.indexOf(day1) - sortedBackendDays.indexOf(day2);
                            })
                            .map(([_, v]) => ({
                                isActivated: v.isActivated,
                                ranges: v.ranges || []
                            }))
                    }
                }
            }))
        );
    }

    fetchAlertScenarios$(
        paginatedParams: CoreApiPaginatedRequestParams,
        periodCode: AlertStatPeriodCode,
        viewAllProjectScenarios: boolean,
        filters: DriftScenarioFilters
    ): Observable<CoreApiPaginatedResponse<DriftScenarioSummary>> {
        interface RouteRequest {
            search: string | null;
            scenarioStatus: AlertScenarioStatus | null;
            targetMeasure: DriftTargetMeasure | null;
            measureLevel: DriftMeasureLevel | null;
        }

        interface RouteResponse {
            alertScenarioId: AlertScenarioId;
            scenarioType: AlertScenarioType;
            name: string;
            universeId: UniverseId;
            targetMeasure: DriftTargetMeasure;
            measureLevel: DriftMeasureLevel;
            isActiveInOccupation: boolean;
            isActiveInInoccupation: boolean;
            status: AlertScenarioStatus;
            access: AccessLevel;
            abruptAlertCount: number;
            impactedBuildingCount: number | null;
            buildingCount: number | null;
            consumptionImpactedBuildingPercent: number | null;
            impactedSensorCount: number | null;
            sensorCount: number | null;
            consumptionImpactedSensorPercent: number | null;
            abruptAlertProgress: number | null;
            impactedBuildingProgress: number | null;
            impactedSensorProgress: number | null;
            alertedVal: number;
            alertedValPercent: number;
            alertedValPercentProgress: number;
        }

        const body: RouteRequest = {
            search: filters.search,
            scenarioStatus: filters.status,
            measureLevel: filters.measureLevel,
            targetMeasure: filters.targetMeasure
        };

        const pagination = convertPaginatedRequestParamsToString(paginatedParams);
        const url = viewAllProjectScenarios ? 'alerts/scenario/drift/search/all' : 'alerts/scenario/drift/search';

        return this._rosaCoreApiService
            .post<CoreApiPaginatedResponse<RouteResponse>>(`${CoreApiVersionPath.V5}${url}?periodCode=${periodCode}&${pagination}`, body)
            .pipe(
                map(paginatedResponse => {
                    const alertScenarios: DriftScenarioSummary[] = paginatedResponse.content.map(scenario => ({
                        alertScenarioId: scenario.alertScenarioId,
                        scenarioType: scenario.scenarioType,
                        name: scenario.name,
                        universeId: scenario.universeId,
                        targetMeasure: scenario.targetMeasure,
                        measureLevel: scenario.measureLevel,
                        occupancy:
                            scenario.isActiveInInoccupation && scenario.isActiveInOccupation
                                ? AlertOccupancyMode.ALL
                                : scenario.isActiveInInoccupation
                                  ? AlertOccupancyMode.INOCCUPANCY
                                  : scenario.isActiveInOccupation
                                    ? AlertOccupancyMode.OCCUPANCY
                                    : AlertOccupancyMode.ALL,
                        status: scenario.status,
                        access: scenario.access,
                        abruptAlertCount: scenario.abruptAlertCount,
                        abruptAlertProgress: scenario.abruptAlertProgress,
                        buildingCount: scenario.buildingCount,
                        impactedBuildingCount: scenario.impactedBuildingCount,
                        impactedBuildingProgress: scenario.impactedBuildingProgress,
                        consumptionImpactedBuildingPercent: scenario.consumptionImpactedBuildingPercent,
                        impactedSensorCount: scenario.impactedSensorCount,
                        sensorCount: scenario.sensorCount,
                        consumptionImpactedSensorPercent: scenario.consumptionImpactedSensorPercent,
                        impactedSensorProgress: scenario.impactedSensorProgress,
                        alertedVal: this._unitProvider.formatterFactory(
                            UnitCode.KWH,
                            this._unitProvider.getBestConversion([scenario.alertedVal], UnitCode.KWH)
                        )(scenario.alertedVal),
                        alertedValPercent: scenario.alertedValPercent,
                        alertedValPercentProgress: scenario.alertedValPercentProgress
                    }));

                    return { ...paginatedResponse, content: alertScenarios };
                })
            );
    }

    updateDriftScenario(alertScenarioId: AlertScenarioId, update: DriftScenarioUpdate): Promise<void> {
        interface RouteRequest {
            name?: string;
            sensitivityLevel?: DriftSensitivityLevel;
            isActiveInOccupation?: boolean;
            isActiveInInoccupation?: boolean;
            isActiveDuringSchoolHolidays?: boolean;
            isActiveDuringPublicHolidays?: boolean;
            periodStart?: {
                day: number;
                month: number;
            };
            periodEnd?: {
                day: number;
                month: number;
            };
            daysRanges?: Record<
                string,
                {
                    isActivated: boolean;
                    ranges: {
                        start: string;
                        end: string;
                    }[];
                }
            >;
        }

        const body: RouteRequest = {
            name: update.name,
            isActiveInOccupation: update.driftConfig?.activity?.occupancy
                ? update.driftConfig?.activity.occupancy === AlertOccupancyMode.ALL ||
                  update.driftConfig?.activity.occupancy === AlertOccupancyMode.OCCUPANCY
                : undefined,
            isActiveInInoccupation: update.driftConfig?.activity?.occupancy
                ? update.driftConfig?.activity?.occupancy === AlertOccupancyMode.ALL ||
                  update.driftConfig?.activity?.occupancy === AlertOccupancyMode.INOCCUPANCY
                : undefined,
            isActiveDuringSchoolHolidays: update.driftConfig?.activity?.alertHolidays,
            isActiveDuringPublicHolidays: update.driftConfig?.activity?.alertPublicHolidays,
            periodStart: update.driftConfig?.activity?.periodStart,
            periodEnd: update.driftConfig?.activity?.periodEnd,
            sensitivityLevel: update.driftConfig?.sensitivityLevel,
            daysRanges: update.driftConfig?.activity?.weeklyRanges
                ? update.driftConfig?.activity?.weeklyRanges.reduce(
                      (acc, currentValue, index) => {
                          return {
                              ...acc,
                              [sortedBackendDays[index]]: { isActivated: currentValue.isActivated, ranges: currentValue.ranges }
                          };
                      },
                      {} as Record<
                          string,
                          {
                              isActivated: boolean;
                              ranges: {
                                  start: string;
                                  end: string;
                              }[];
                          }
                      >
                  )
                : undefined
        };

        return firstValueFrom(this._rosaCoreApiService.put<void>(`${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}/drift`, body));
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/save`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/publish`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    getAlertScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        interface RouteResponse {
            errors: APIError[];
            valid: boolean;
        }

        return this._rosaCoreApiService
            .get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/check`)
            .pipe(restartOn(this._alertScenarioSaved$.pipe(filter(savedScenarioId => savedScenarioId === alertScenarioId))));
    }

    getSensorAnalysis$(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDate,
        endDate: UnzonedDate
    ): Observable<DriftAlertsAnalysis> {
        interface RouteRequest {
            startDate: string;
            endDate: string;
        }

        const body: RouteRequest = { startDate: unzonedDateToApiDateFormat(startDate), endDate: unzonedDateToApiDateFormat(endDate) };

        interface RouteResponse {
            targetMeasureTimeseries: { datetime: UnzonedDateTime; value: number | null }[];
            targetMeasureSourceTimeseries: { datetime: UnzonedDateTime; value: number }[];
            scenarioActivity: { datetime: UnzonedDateTime; activeAfter: boolean; inactivityStatuses: string[] }[];
            abruptAlerts: { alertTypeCode: 'abrupt_drift_up' | 'abrupt_drift_down'; status: string; occurredAt: UnzonedDateTime }[];
            gradualAlerts: {
                alertTypeCode: 'gradual_drift_up' | 'gradual_drift_down';
                status: string;
                startedAt: UnzonedDateTime;
                endedAt: UnzonedDateTime;
            }[];
        }

        return this._rosaCoreApiService
            .post<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/drift/analysis/sen/${timeseriesId}`, body)
            .pipe(
                map(response => ({
                    ...response,
                    targetMeasureSourceTimeseriesUnitCode: UnitCode.KW,
                    targetMeasureTimeseriesUnitCode: UnitCode.PERCENT
                }))
            );
    }

    getBuildingAnalysis$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDate,
        endDate: UnzonedDate
    ): Observable<DriftAlertsAnalysis> {
        interface RouteRequest {
            startDate: string;
            endDate: string;
        }

        const body: RouteRequest = { startDate: unzonedDateToApiDateFormat(startDate), endDate: unzonedDateToApiDateFormat(endDate) };

        interface RouteResponse {
            targetMeasureTimeseries: { datetime: UnzonedDateTime; value: number | null }[];
            targetMeasureSourceTimeseries: { datetime: UnzonedDateTime; value: number }[];
            scenarioActivity: { datetime: UnzonedDateTime; activeAfter: boolean; inactivityStatuses: string[] }[];
            abruptAlerts: { alertTypeCode: 'abrupt_drift_up' | 'abrupt_drift_down'; status: string; occurredAt: UnzonedDateTime }[];
            gradualAlerts: {
                alertTypeCode: 'gradual_drift_up' | 'gradual_drift_down';
                status: string;
                startedAt: UnzonedDateTime;
                endedAt: UnzonedDateTime;
            }[];
        }

        return this._rosaCoreApiService
            .post<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/drift/analysis/building/${buildingId}`, body)
            .pipe(
                map(response => ({
                    ...response,
                    targetMeasureSourceTimeseriesUnitCode: UnitCode.KW,
                    targetMeasureTimeseriesUnitCode: UnitCode.PERCENT
                }))
            );
    }

    getDriftGlobalStats$(periodCode: AlertStatPeriodCode): Observable<DriftGlobalStats> {
        interface RouteResponse {
            abruptAlertCount: number;
            impactedBuildingCount: number;
            buildingCount: number;
            buildingConsumption: number;
            impactedBuildingConsumption: number;
            impactedSensorCount: number;
            sensorCount: number;
            sensorConsumption: number;
            impactedSensorConsumption: number;
        }

        return this._rosaCoreApiService.get<RouteResponse>(
            `${CoreApiVersionPath.V5}alerts/scenario/drift/global-stats?periodCode=${periodCode}`
        );
    }

    fetchGradualAlertScenarioBuildingsStatsForBuildingLevel$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<DriftScenarioBuildingLevelGradualStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            alertCount: number;
            alertedValPercent: number;
            alertedVal: number;
            activeScenarioVal: number;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);
        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<RouteResponse>
            >(`${CoreApiVersionPath.V5}alerts/scenarios/drift/building-level/${alertScenarioId}/buildings-gradual-stats/search?periodCode=${periodCode}&${params}`, { search })
            .pipe(
                map(paginatedResponse => {
                    const alertStats: DriftScenarioBuildingLevelGradualStat[] = paginatedResponse.content.map(stat => ({
                        buildingId: stat.buildingId,
                        activeScenarioVal: stat.activeScenarioVal,
                        alertedVal: stat.alertedVal,
                        formattedAlertedVal: this._unitProvider.formatterFactory(
                            UnitCode.KWH,
                            this._unitProvider.getBestConversion([stat.alertedVal], UnitCode.KWH)
                        )(stat.alertedVal),
                        alertedValPercent: stat.alertedValPercent
                    }));

                    return { ...paginatedResponse, content: alertStats };
                })
            );
    }

    fetchAbruptAlertScenarioBuildingsStatsForBuildingLevel$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<DriftScenarioBuildingLevelAbruptStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            alertCount: number;
            abruptAlertCount: number;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);
        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<RouteResponse>
            >(`${CoreApiVersionPath.V5}alerts/scenarios/drift/building-level/${alertScenarioId}/buildings-abrupt-stats/search?periodCode=${periodCode}&${params}`, { search })
            .pipe(
                map(paginatedResponse => {
                    const alertStats: DriftScenarioBuildingLevelAbruptStat[] = paginatedResponse.content.map(stat => ({
                        buildingId: stat.buildingId,
                        abruptAlertCount: stat.abruptAlertCount
                    }));

                    return { ...paginatedResponse, content: alertStats };
                })
            );
    }

    fetchGradualAlertScenarioBuildingsStatsForSenLevel$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<DriftScenarioSenLevelGradualStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            sensorCount: number;
            alertCount: number;
            alertedValPercent: number;
            alertedVal: number;
            activeScenarioVal: number;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);

        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<RouteResponse>
            >(`${CoreApiVersionPath.V5}alerts/scenarios/drift/sen-level/${alertScenarioId}/buildings-gradual-stats/search?periodCode=${periodCode}&${params}`, { search })
            .pipe(
                map(paginatedResponse => {
                    const alertStats: DriftScenarioSenLevelGradualStat[] = paginatedResponse.content.map(stat => ({
                        buildingId: stat.buildingId,
                        sensorCount: stat.sensorCount,
                        alertCount: stat.alertCount,
                        activeScenarioVal: stat.activeScenarioVal,
                        alertedVal: stat.alertedVal,
                        formattedAlertedVal: this._unitProvider.formatterFactory(
                            UnitCode.KWH,
                            this._unitProvider.getBestConversion([stat.alertedVal], UnitCode.KWH)
                        )(stat.alertedVal),
                        alertedValPercent: stat.alertedValPercent
                    }));

                    return { ...paginatedResponse, content: alertStats };
                })
            );
    }

    fetchAbruptAlertScenarioBuildingsStatsForSenLevel$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<DriftScenarioSenLevelAbruptStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            sensorCount: number;
            alertCount: number;
            abruptAlertCount: number;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);

        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<RouteResponse>
            >(`${CoreApiVersionPath.V5}alerts/scenarios/drift/sen-level/${alertScenarioId}/buildings-abrupt-stats/search?periodCode=${periodCode}&${params}`, { search })
            .pipe(
                map(paginatedResponse => {
                    const alertStats: DriftScenarioSenLevelAbruptStat[] = paginatedResponse.content.map(stat => ({
                        buildingId: stat.buildingId,
                        sensorCount: stat.sensorCount,
                        alertCount: stat.alertCount,
                        abruptAlertCount: stat.abruptAlertCount
                    }));

                    return { ...paginatedResponse, content: alertStats };
                })
            );
    }

    fetchAlertScenarioSensorStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<DriftScenarioSensorStat[]> {
        interface RouteResponse {
            timeseriesId: SensorTimeseriesId;
        }
        return this._rosaCoreApiService.post<RouteResponse[]>(
            `${CoreApiVersionPath.V5}alerts/scenarios/drift/${alertScenarioId}/buildings/${buildingId}/sensors-stats`,
            {
                startDate: unzonedDateTimeToApiDateFormat(startDate),
                endDate: unzonedDateTimeToApiDateFormat(momentDateToUnzonedDateTime(unzonedDateTimeToMomentDate(endDate).add(1, 'day')))
            }
        );
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<DriftScenarioSummary> {
        return this._rosaCoreApiService
            .get<DriftScenarioSummaryResponse>(
                `${CoreApiVersionPath.V5}alerts/scenarios/drift/${alertScenarioId}/summary?periodCode=${periodCode}`
            )
            .pipe(map(alert => this.mapToAlertScenarioSummary(alert)));
    }

    private mapToAlertScenarioSummary(scenario: DriftScenarioSummaryResponse): DriftScenarioSummary {
        let occupancy = AlertOccupancyMode.ALL;
        if (scenario.isActiveInInoccupation && !scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.INOCCUPANCY;
        } else if (!scenario.isActiveInInoccupation && scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.OCCUPANCY;
        }

        return {
            alertScenarioId: scenario.alertScenarioId,
            scenarioType: scenario.scenarioType,
            name: scenario.name,
            universeId: scenario.universeId,
            targetMeasure: scenario.targetMeasure,
            measureLevel: scenario.measureLevel,
            occupancy,
            abruptAlertCount: scenario.abruptAlertCount,
            impactedBuildingCount: scenario.impactedBuildingCount,
            buildingCount: scenario.buildingCount,
            consumptionImpactedBuildingPercent: scenario.consumptionImpactedBuildingPercent,
            impactedSensorCount: scenario.impactedSensorCount,
            sensorCount: scenario.sensorCount,
            consumptionImpactedSensorPercent: scenario.consumptionImpactedSensorPercent,
            status: scenario.status,
            access: scenario.access,
            abruptAlertProgress: scenario.abruptAlertProgress,
            impactedBuildingProgress: scenario.impactedBuildingProgress,
            impactedSensorProgress: scenario.impactedSensorProgress,
            alertedVal: this._unitProvider.formatterFactory(
                UnitCode.KWH,
                this._unitProvider.getBestConversion([scenario.alertedVal], UnitCode.KWH)
            )(scenario.alertedVal),
            alertedValPercent: scenario.alertedValPercent,
            alertedValPercentProgress: scenario.alertedValPercentProgress
        };
    }
}
